import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import { PageLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Calendar, Users, ChevronRight, Award, X } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "present", label: "Present", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { value: "late", label: "Late", color: "bg-amber-100 text-amber-700 border-amber-300" },
  { value: "absent", label: "Absent", color: "bg-rose-100 text-rose-700 border-rose-300" },
  { value: "excused", label: "Excused", color: "bg-slate-100 text-slate-700 border-slate-300" },
];

const AttendanceModal = ({ session, cohortRoster, onClose, onSaved }) => {
  const [marks, setMarks] = useState(() => Object.fromEntries(
    cohortRoster.map(r => [r.user_id, { status: "present", skills_demonstrated: [], notes: "" }])
  ));
  const [skillInputs, setSkillInputs] = useState({});
  const [saving, setSaving] = useState(false);

  // Pre-load any existing attendance for this session
  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${API}/instructor/sessions/${session.session_id}/attendance`, { credentials: "include" });
      if (res.ok) {
        const recs = await res.json();
        setMarks(prev => {
          const next = { ...prev };
          recs.forEach(r => { next[r.user_id] = { status: r.status, skills_demonstrated: r.skills_demonstrated || [], notes: r.notes || "" }; });
          return next;
        });
      }
    };
    load();
  }, [session.session_id]);

  const updateMark = (userId, patch) => {
    setMarks(prev => ({ ...prev, [userId]: { ...prev[userId], ...patch } }));
  };

  const addSkill = (userId) => {
    const skill = (skillInputs[userId] || "").trim();
    if (!skill) return;
    const current = marks[userId]?.skills_demonstrated || [];
    if (current.includes(skill)) return;
    updateMark(userId, { skills_demonstrated: [...current, skill] });
    setSkillInputs(prev => ({ ...prev, [userId]: "" }));
  };

  const removeSkill = (userId, skill) => {
    updateMark(userId, { skills_demonstrated: marks[userId].skills_demonstrated.filter(s => s !== skill) });
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = Object.entries(marks).map(([user_id, m]) => ({ user_id, ...m }));
    const res = await fetch(`${API}/instructor/sessions/${session.session_id}/attendance`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast.success(`Attendance saved for ${payload.length} participants`);
      onSaved && onSaved();
      onClose();
    } else {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="attendance-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{session.title}</DialogTitle>
          <p className="text-sm text-slate-500">{new Date(session.session_date).toLocaleDateString()} · {session.program_name}</p>
        </DialogHeader>
        <div className="space-y-3 mt-3">
          {cohortRoster.length === 0 && (
            <p className="text-center py-8 text-slate-500">No enrolled participants in this cohort.</p>
          )}
          {cohortRoster.map((r) => {
            const m = marks[r.user_id] || { status: "present", skills_demonstrated: [], notes: "" };
            return (
              <Card key={r.user_id} className="card-surface" data-testid={`attendance-row-${r.user_id}`}>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </div>
                    <div className="flex gap-1.5">
                      {STATUS_OPTIONS.map(s => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => updateMark(r.user_id, { status: s.value })}
                          className={`px-2.5 py-1 text-xs rounded-full border-2 ${m.status === s.value ? s.color : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}
                          data-testid={`mark-${r.user_id}-${s.value}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={skillInputs[r.user_id] || ""}
                      onChange={(e) => setSkillInputs(prev => ({ ...prev, [r.user_id]: e.target.value }))}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSkill(r.user_id))}
                      placeholder="Add a skill demonstrated this session…"
                      className="input-base text-sm"
                      data-testid={`skill-input-${r.user_id}`}
                    />
                    <Button type="button" size="sm" variant="outline" onClick={() => addSkill(r.user_id)}><Plus className="w-4 h-4" /></Button>
                  </div>
                  {m.skills_demonstrated.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.skills_demonstrated.map(s => (
                        <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#FFD507]/15 text-[#1E3A8A] rounded-full">
                          {s}
                          <button onClick={() => removeSkill(r.user_id, s)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-attendance-btn">
            {saving ? "Saving…" : "Save Attendance"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ClassesPage = () => {
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [roster, setRoster] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newSession, setNewSession] = useState({ title: "", session_date: new Date().toISOString().slice(0, 10), notes: "" });
  const navigate = useNavigate();

  // Load all programs/cohorts (anyone can see, instructors filter)
  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${API}/programs`);
      if (res.ok) {
        const programs = await res.json();
        const flat = [];
        programs.forEach(p => (p.cohorts || []).forEach(c => flat.push({ ...c, program_name: p.name, program_id: p.program_id })));
        flat.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        setCohorts(flat);
        if (flat.length && !selectedCohortId) setSelectedCohortId(flat[0].cohort_id);
      }
    };
    load();
  }, []);  // eslint-disable-line

  // Load sessions + roster when cohort selected
  useEffect(() => {
    if (!selectedCohortId) return;
    const load = async () => {
      const [sRes, rRes] = await Promise.all([
        fetch(`${API}/instructor/cohorts/${selectedCohortId}/sessions`, { credentials: "include" }),
        fetch(`${API}/admin/cohorts/${selectedCohortId}/roster`, { credentials: "include" }),
      ]);
      if (sRes.ok) setSessions(await sRes.json());
      if (rRes.ok) {
        const d = await rRes.json();
        setRoster(d.roster || []);
      }
    };
    load();
  }, [selectedCohortId]);

  const handleCreateSession = async () => {
    if (!newSession.title.trim()) { toast.error("Title required"); return; }
    const res = await fetch(`${API}/instructor/cohorts/${selectedCohortId}/sessions`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ cohort_id: selectedCohortId, ...newSession }),
    });
    if (res.ok) {
      const created = await res.json();
      setSessions([created, ...sessions]);
      setNewSession({ title: "", session_date: new Date().toISOString().slice(0, 10), notes: "" });
      setCreating(false);
      toast.success("Session created. Now mark attendance.");
      setActiveSession(created);
    } else {
      toast.error("Failed to create session");
    }
  };

  const selectedCohort = cohorts.find(c => c.cohort_id === selectedCohortId);

  return (
    <PageLayout title="Live Classes" subtitle="Track attendance and skills earned per session. Export reports anytime.">
      <div className="space-y-5" data-testid="classes-page">
        {/* Cohort selector */}
        <Card className="card-surface">
          <CardContent className="pt-4 pb-4">
            <Label className="text-slate-600">Cohort</Label>
            <select
              value={selectedCohortId || ""}
              onChange={(e) => setSelectedCohortId(e.target.value)}
              className="input-base mt-1"
              data-testid="cohort-select"
            >
              {cohorts.map(c => (
                <option key={c.cohort_id} value={c.cohort_id}>
                  {c.program_name} — {new Date(c.start_date).toLocaleDateString()} ({(c.instructors || []).join(", ") || "TBD"})
                </option>
              ))}
            </select>
            {selectedCohort && (
              <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
                <p className="text-sm text-slate-500">
                  <Users className="w-4 h-4 inline mr-1" /> {roster.length} enrolled · {selectedCohort.duration_weeks}-week program
                </p>
                <Link to={`/admin/cohorts/${selectedCohortId}/roster`}>
                  <Button variant="outline" size="sm">Open full roster →</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New session card */}
        <Card className="card-surface">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-slate-900 text-lg">Class Sessions</CardTitle>
              {!creating && (
                <Button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-2" data-testid="new-session-btn">
                  <Plus className="w-4 h-4" /> New Session
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {creating && (
              <div className="bg-slate-50 p-4 rounded-lg space-y-3 border border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-600">Session title *</Label>
                    <Input value={newSession.title} onChange={(e) => setNewSession({ ...newSession, title: e.target.value })} placeholder="e.g., Week 1: Intro to Test Plans" className="input-base mt-1" data-testid="new-session-title" />
                  </div>
                  <div>
                    <Label className="text-slate-600">Date</Label>
                    <Input type="date" value={newSession.session_date} onChange={(e) => setNewSession({ ...newSession, session_date: e.target.value })} className="input-base mt-1" />
                  </div>
                </div>
                <Textarea value={newSession.notes} onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })} placeholder="Session notes (optional)" className="input-base text-sm" />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                  <Button onClick={handleCreateSession} className="btn-primary" data-testid="save-session-btn">Create Session</Button>
                </div>
              </div>
            )}
            {sessions.length === 0 && !creating && (
              <p className="text-center py-8 text-slate-400">No sessions yet for this cohort. Click "New Session" to create one.</p>
            )}
            {sessions.map((s) => (
              <Card key={s.session_id} className="border border-slate-200 hover:border-[#FFD507] cursor-pointer" onClick={() => setActiveSession(s)} data-testid={`session-${s.session_id}`}>
                <CardContent className="pt-3 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#1E3A8A]" />
                    <div>
                      <p className="font-semibold text-slate-900">{s.title}</p>
                      <p className="text-xs text-slate-500">{new Date(s.session_date).toLocaleDateString()} · {s.program_name}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center">
          Tip: skills you tag here roll up into each participant's attendance PDF & Talent DB profile.
        </p>
      </div>

      {activeSession && (
        <AttendanceModal
          session={activeSession}
          cohortRoster={roster}
          onClose={() => setActiveSession(null)}
          onSaved={() => {/* could refetch summary later */}}
        />
      )}
    </PageLayout>
  );
};

export default ClassesPage;
